<template>
  <div class="card mb-4">
    <div class="card-body">
      <h4>{{ editMode ? 'Editar Suscripción' : 'Nueva Suscripción' }}</h4>
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-12 mb-3">
            <label class="form-label">Concepto *</label>
            <input 
              type="text" 
              class="form-control" 
              v-model="formData.concepto"
              required
              placeholder="Ej: Netflix, Gimnasio, etc."
            >
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">{{ editMode ? 'Próximo Pago' : 'Fecha Inicio' }}</label>
            <input
              type="date"
              class="form-control"
              v-model="formData.fecha_inicio"
              required
              :min="fechaMinima"
              :class="{ 'is-invalid': errorFechaInicio || errorFechaInicioPasada }"
            >
            <div class="invalid-feedback" v-if="errorFechaInicio">
              La fecha de inicio no puede ser anterior a hoy
            </div>
            <div class="invalid-feedback" v-if="errorFechaInicioPasada">
              El próximo pago no puede ser una fecha pasada
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Fecha Fin (opcional)</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="formData.fecha_fin"
              :min="formData.fecha_inicio"
              :class="{ 'is-invalid': errorFechaFin }"
            >
            <div class="invalid-feedback" v-if="errorFechaFin">
              La fecha de fin no puede ser anterior a la fecha de inicio
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Cantidad</label>
            <input 
              type="number" 
              step="0.01" 
              class="form-control" 
              v-model="formData.cantidad"
              required
            >
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">División</label>
            <select class="form-select" v-model="formData.tipo_division" required>
              <option value="total">Total</option>
              <option value="mitad">Mitad</option>
            </select>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Periodicidad</label>
            <select class="form-select" v-model="formData.periodicidad" required>
              <option value="diario">Diario</option>
              <option value="semanal">Semanal</option>
              <option value="mensual">Mensual</option>
              <option value="anual">Anual</option>
            </select>
          </div>
          <div class="col-md-4 mb-3" v-if="formData.periodicidad === 'mensual' || formData.periodicidad === 'anual'">
            <label class="form-label">Día del mes</label>
            <select class="form-select" v-model="formData.dia_mes" :class="{ 'is-invalid': errorDiaMes }">
              <option v-for="d in 28" :key="d" :value="String(d)">{{ d }}</option>
            </select>
            <div class="invalid-feedback" v-if="errorDiaMes">
              El día debe estar entre 1 y 28
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Categoría</label>
            <select class="form-select" v-model="formData.categoria_id" required>
              <option value="">Selecciona una categoría</option>
              <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                {{ cat.nombre }}
              </option>
            </select>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Recordatorio (días)</label>
            <input 
              type="number" 
              class="form-control" 
              v-model="formData.recordatorio"
              min="0"
            >
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Modo de Pago</label>
            <select class="form-select" v-model="formData.modo_pago" required>
              <option value="manual">Manual</option>
              <option value="automatico">Automático</option>
            </select>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Observaciones</label>
            <textarea 
              class="form-control" 
              v-model="formData.observaciones"
              rows="3"
            ></textarea>
          </div>
          <div class="col-12 mb-3">
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                v-model="formData.activa"
                id="activaCheck"
              >
              <label class="form-check-label" for="activaCheck">
                Suscripción Activa
              </label>
            </div>
          </div>
        </div>
        <div class="d-flex gap-2">
          <button type="submit" class="btn btn-primary">
            {{ editMode ? 'Actualizar' : 'Guardar' }}
          </button>
          <button type="button" class="btn btn-secondary" @click="resetForm">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';

export default {
  name: 'SuscripcionForm',
  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    suscripcionToEdit: {
      type: Object,
      default: null
    }
  },
  
  emits: ['suscripcion-added', 'suscripcion-updated', 'cancel-edit'],

  setup(props, { emit }) {
    const store = useStore();
    const errorDiaMes = ref(false);
    const errorFechaInicio = ref(false);
    const errorFechaInicioPasada = ref(false);
    const errorFechaFin = ref(false);
    const loading = ref(false);
    const error = ref(null);
    
    // Cambiar de computed a ref para cargar las categorías explícitamente
    const categorias = ref([]);
    
    // Función para cargar las categorías
    const loadCategorias = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        const response = await axios.get('/api/categorias');
        categorias.value = response.data;
        console.log('Categorías cargadas:', categorias.value);
      } catch (err) {
        console.error('Error al cargar categorías:', err);
        error.value = 'Error al cargar las categorías';
      } finally {
        loading.value = false;
      }
    };
    
    // Obtener la fecha actual en formato YYYY-MM-DD
    const fechaActual = new Date().toISOString().split('T')[0];

    const resetForm = () => {
      formData.value = {
        concepto: '',
        fecha_inicio: fechaActual,
        fecha_fin: '',
        dia_mes: '1',
        cantidad: '',
        tipo_division: 'total',
        periodicidad: 'mensual',
        categoria_id: '',
        recordatorio: '0',
        observaciones: '',
        activa: true,
        modo_pago: 'manual'
      };
      if (props.editMode) {
        emit('cancel-edit');
      }
    };

    const formData = ref({
      concepto: '',
      fecha_inicio: fechaActual,
      fecha_fin: '',
      dia_mes: 1,
      cantidad: '',
      tipo_division: 'total',
      periodicidad: 'mensual',
      categoria_id: '',
      recordatorio: 0,
      observaciones: '',
      activa: true,
      modo_pago: 'manual'
    });

    // Obtener la fecha actual en formato YYYY-MM-DD
    const fechaMinima = computed(() => {
      const hoy = new Date();
      return hoy.toISOString().split('T')[0];
    });

    // Watch para validar la fecha cuando cambie
    watch(() => formData.value.fecha_inicio, (newDate) => {
      if (newDate) {
        const fecha = new Date(newDate);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        if (props.editMode) {
          // En edición: "Próximo Pago" no puede ser en el pasado
          errorFechaInicio.value = false;
          errorFechaInicioPasada.value = fecha < hoy;
        } else {
          errorFechaInicio.value = fecha < hoy;
          errorFechaInicioPasada.value = false;
        }
      } else {
        errorFechaInicio.value = false;
        errorFechaInicioPasada.value = false;
      }
    });

    // Watch para validar la fecha de fin cuando cambie
    watch([() => formData.value.fecha_fin, () => formData.value.fecha_inicio], ([newFechaFin, newFechaInicio]) => {
      if (newFechaFin && newFechaInicio) {
        const fechaFin = new Date(newFechaFin);
        const fechaInicio = new Date(newFechaInicio);
        errorFechaFin.value = fechaFin < fechaInicio;
      } else {
        errorFechaFin.value = false;
      }
    });

    watch(() => props.suscripcionToEdit, (newValue) => {
      if (newValue) {
        // Limpiar errores de validación antes de cargar los datos de edición
        errorFechaInicio.value = false;
        errorFechaInicioPasada.value = false;
        errorFechaFin.value = false;
        formData.value = {
          ...newValue,
          concepto: newValue.concepto || '',
          cantidad: String(newValue.cantidad),
          dia_mes: String(newValue.dia_mes ?? 1),
          recordatorio: String(newValue.recordatorio),
          categoria_id: newValue.categoria_id,
          fecha_inicio: (newValue.proximo_pago?.split('T')[0]) || newValue.fecha_inicio?.split('T')[0] || '',
          fecha_fin: newValue.fecha_fin?.split('T')[0] || '',
          activa: Boolean(newValue.activa),
          modo_pago: newValue.modo_pago || 'manual'
        };
        console.log('Datos del formulario:', formData.value);
      } else {
        resetForm();
      }
    }, { immediate: true });

    const handleSubmit = async () => {
      if (errorFechaInicio.value || errorFechaInicioPasada.value || errorFechaFin.value) {
        return;
      }
      
      try {
        validarDiaMes();
        if (errorDiaMes.value) {
          return;
        }

        if (!formData.value.concepto?.trim()) {
          alert('El concepto es obligatorio');
          return;
        }

        if (!formData.value.cantidad || parseFloat(formData.value.cantidad) <= 0) {
          alert('La cantidad debe ser mayor que 0');
          return;
        }

        if (!formData.value.categoria_id) {
          alert('Debe seleccionar una categoría');
          return;
        }

        const dataToSend = {
          ...formData.value,
          concepto: formData.value.concepto.trim(),
          cantidad: parseFloat(formData.value.cantidad),
          dia_mes: parseInt(formData.value.dia_mes),
          recordatorio: parseInt(formData.value.recordatorio || 0),
          fecha_fin: formData.value.fecha_fin || null,
          observaciones: formData.value.observaciones?.trim() || '',
          modo_pago: formData.value.modo_pago
        };

        console.log('Datos a enviar:', dataToSend);

        if (props.editMode) {
          const response = await axios.put(`/api/suscripciones/${props.suscripcionToEdit.id}`, dataToSend);
          console.log('Respuesta del servidor:', response.data);
        } else {
          const response = await axios.post('/api/suscripciones', dataToSend);
          console.log('Respuesta del servidor:', response.data);
        }

        emit(props.editMode ? 'suscripcion-updated' : 'suscripcion-added');
        resetForm();
      } catch (error) {
        console.error('Error completo:', error);
        console.error('Respuesta del servidor:', error.response?.data);
        alert('Error al guardar la suscripción');
      }
    };

    const validarDiaMes = () => {
      const dia = parseInt(formData.value.dia_mes);
      if (dia < 1 || dia > 28) {
        errorDiaMes.value = true;
        formData.value.dia_mes = Math.min(Math.max(dia, 1), 28);
      } else {
        errorDiaMes.value = false;
      }
    };

    onMounted(() => {
      // Cargar categorías al montar el componente
      loadCategorias();
      
      if (props.suscripcionToEdit) {
        formData.value = {
          ...props.suscripcionToEdit,
          activa: Boolean(props.suscripcionToEdit.activa),
          modo_pago: props.suscripcionToEdit.modo_pago || 'manual',
          fecha_inicio: (props.suscripcionToEdit.proximo_pago?.split('T')[0]) || props.suscripcionToEdit.fecha_inicio?.split('T')[0] || ''
        };
      }
    });

    return {
      formData,
      categorias,
      errorDiaMes,
      errorFechaInicio,
      errorFechaInicioPasada,
      errorFechaFin,
      fechaMinima,
      handleSubmit,
      resetForm,
      validarDiaMes,
      loading,
      error
    };
  }
};
</script>

<style scoped>
.invalid-feedback {
  display: block !important;
}
</style> 